import { LoggerService } from '../services';
import { TaggedLogger } from '../services/taggedLogger';
import { nanoid } from 'nanoid';

export interface AsyncTask {
  id: string;
  source: string;
  timestamp: number;
}

// We probably want a way to debug these
declare global {
  interface Window {
    dumpLoaders: () => void;
  }
}

/**
 * A replacement for the isBusy boolean structure.
 * i.e. on a component I see isBusy=true/isBusy=false used to determine if there's changes being saved or data being loaded.
 * The problem with this methodology is we can't use multiple methods that set isBusy at the same time.
 * For example, on OrganisationSpecificStepComponent we have a load method in ngOnInit which:
 * - loads the Customer object
 * - loads Checklists
 * Both set is busy, and don't affect each other, so they can be resolved in a promise.all, however. When one resolves, it will set isBusy to false, while the other request is still ongoing.
 *
 * Expected Usage:
 * // ...constructor
 * this.loader = Loader.create('component-name/service-name', loggerService);
 *
 * // Async Method
 * const taskId = this.loader.addTask('source, i.e. the method or an okay tag')
 * //do async work, try, catch, finally
 * // In the finally:
 * this.loader.completeTask(taskId);
 *
 * note: have left the inbuilt limitation that multiple instances of the same component can't use this.
 * If that needs to change, same short-id would work on the static loaders instance.
 */
export class Loader {
  /**
   * Debugging messages whilst working on this to make sure it does what I expect
   * @private
   */
  private static readonly debug = false;
  private static loaders: Loader[] = [];
  private static logger: TaggedLogger;

  private tasks: AsyncTask[] = [];

  private constructor(private name: string = '') {}

  addTask(source: string): string {
    const id = nanoid();

    this.tasks.push({
      id,
      source,
      timestamp: new Date().getTime(),
    });

    if (Loader.debug) {
      this.log(`Add ${source}: ${id}`);
      Loader.dump();
    }
    return id;
  }

  completeTask(id: string) {
    const index = this.tasks.findIndex((t) => t.id === id);
    if (index === -1) {
      this.log('Task completed but could not find in task list', id);
      Loader.dump();
      return;
    }

    this.tasks.splice(index, 1);
    if (Loader.debug) {
      this.log(`Complete ${id}`);
      Loader.dump();
    }
  }

  get isBusy(): boolean {
    return !!this.tasks.length;
  }

  static create(name: string, loggerService: LoggerService = null): Loader {
    if (!name) throw new Error('Called Loader.create() without a name');

    if (!this.logger && loggerService) this.logger = loggerService.taggedLogger('util:loader');

    const existing = this.loaders.find((loader) => loader.name === name);

    if (existing) {
      // Reset it if it exists
      existing.tasks = [];
      if (Loader.debug) {
        this.log(`Return existing Loader ${name}`);
        Loader.dump();
      }
      return existing;
    }

    const newLoader = new Loader(name);
    this.loaders.push(newLoader);
    if (Loader.debug) {
      this.log(`Create new Loader ${name}`);
      Loader.dump();
    }
    return newLoader;
  }

  /**
   * Dumps loaders, useful for debugging purposes
   */
  static dump() {
    let dumpString = '';
    for (const loader of this.loaders) {
      dumpString += `${loader.name}:\n`;
      for (const task of loader.tasks) {
        dumpString += `\t- ${task.source} ${task.id} at ${task.timestamp}\n`;
      }
    }
    this.log(dumpString);
  }

  /**
   * Write a log
   * @param message The log message
   * @param args The additional args
   * @private
   */
  private static log(message: string, ...args: any[]) {
    if (!this.logger) {
      console.log(message, ...args);
    } else {
      this.logger.debug(message, ...args);
    }
  }
  /**
   * Write a log
   * @param message The log message
   * @param args The additional args
   * @private
   */
  private log(message: string, ...args: any[]) {
    Loader.log(message, ...args);
  }
}

window.dumpLoaders = () => Loader.dump();
